import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Divider,
  Group,
  Notification,
  Paper,
  ScrollArea,
  Skeleton,
  Space,
  Stack,
  Switch,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { NavigationWrapper } from '../../../components/navigation/NavigationWrapper';
import { PageRoutes } from '../../../constants/routes';
import { useApp } from '../../../context/AppContext';
import { primaryColor } from '../../../constants/theme';
import {
  FollowingCreator,
  getFollowing,
  toggleAvailabilityNotifications,
} from '../../../services/FollowingService';
import {
  setCreatorAvailabilityNotificationInProfile,
} from '../../../services/followingProfile';
import { getProfilePicture } from '../../../types/UserDocument';

const FOLLOWING_PAGE_SIZE = 20;

const FollowingPageSkeleton = ({ count = 8 }: { count?: number }) => (
  <Stack gap={0} data-testid="following-loading">
    {Array.from({ length: count }).map((_, i) => (
      <Group key={i} justify="space-between" align="center" py="sm" px="xs">
        <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
          <Skeleton height={56} circle />
          <Stack gap={8} align="flex-start" style={{ flex: 1 }}>
            <Skeleton height={16} width="40%" radius="xl" />
            <Skeleton height={12} width="70%" radius="xl" />
          </Stack>
        </Group>
        <Skeleton height={24} width={44} radius="xl" />
      </Group>
    ))}
  </Stack>
);

interface FollowerRowProps {
  creator: FollowingCreator;
  onNavigate: (id: string) => void;
  onToggleChange: (id: string, enabled: boolean) => void;
  checked: boolean;
  disabled: boolean;
}

const FollowerRow = ({
  creator,
  onNavigate,
  onToggleChange,
  checked,
  disabled,
}: FollowerRowProps) => (
  <Group justify="space-between" align="center" py="sm" px="xs">
    <UnstyledButton
      onClick={() => onNavigate(creator.id)}
      style={{ flex: 1 }}
      data-testid={`following-row-${creator.id}`}
    >
      <Group gap="sm" wrap="nowrap">
        <Avatar
          src={getProfilePicture(creator.profilePictureUrl || undefined)}
          radius="xl"
          size={56}
        />
        <Stack gap={0} align="flex-start">
          <Text fw={500} ta="left">
            {creator.displayName}
          </Text>
          {creator.headline && (
            <Text size="sm" c="dimmed" ta="left">
              {creator.headline}
            </Text>
          )}
        </Stack>
      </Group>
    </UnstyledButton>
    <Switch
      checked={checked}
      color={primaryColor}
      onChange={(event) => onToggleChange(creator.id, event.currentTarget.checked)}
      disabled={disabled}
    />
  </Group>
);

export const FollowingPage = () => {
  const { scrollAreaHeight, profile, onSetProfile, isTabletAndBelow } = useApp();
  const navigate = useNavigate();
  const viewportRef = useRef<HTMLDivElement>(null);
  const [localToggleOverrides, setLocalToggleOverrides] = useState<
    Record<string, boolean>
  >({});
  const [toggleErrorToast, setToggleErrorToast] = useState('');

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['followingList'],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => getFollowing(pageParam, FOLLOWING_PAGE_SIZE),
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? lastPage.pageNumber + 1 : undefined,
  });

  useEffect(() => {
    if (!toggleErrorToast) {
      return;
    }

    const timeoutId = window.setTimeout(() => setToggleErrorToast(''), 3500);

    return () => window.clearTimeout(timeoutId);
  }, [toggleErrorToast]);

  useEffect(() => {
    const container = viewportRef.current;

    if (!container) {
      return;
    }

    const onScroll = () => {
      const threshold = 120;
      const reachedEnd =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - threshold;

      if (reachedEnd && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };

    container.addEventListener('scroll', onScroll);

    return () => {
      container.removeEventListener('scroll', onScroll);
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const creators = useMemo(
    () => data?.pages.flatMap((page) => page.professionals) ?? [],
    [data]
  );

  const getToggleValue = (creator: FollowingCreator): boolean => {
    const localOverride = localToggleOverrides[creator.id];

    if (typeof localOverride === 'boolean') {
      return localOverride;
    }

    return creator.availabilityNotificationsEnabled;
  };

  const { mutate: mutateToggle, isPending: togglePending } = useMutation({
    mutationFn: async ({
      professionalId,
      enabled,
    }: {
      professionalId: string;
      enabled: boolean;
    }) => {
      await toggleAvailabilityNotifications(professionalId, enabled);
    },
    onError: (_error, variables) => {
      setLocalToggleOverrides((current) => ({
        ...current,
        [variables.professionalId]: !variables.enabled,
      }));

      if (profile) {
        onSetProfile(
          setCreatorAvailabilityNotificationInProfile(
            profile,
            variables.professionalId,
            !variables.enabled
          )
        );
      }

      setToggleErrorToast(
        'Failed to update availability notifications. Please try again.'
      );
    },
  });

  const handleToggleChange = (professionalId: string, enabled: boolean) => {
    setLocalToggleOverrides((current) => ({
      ...current,
      [professionalId]: enabled,
    }));

    if (profile) {
      onSetProfile(
        setCreatorAvailabilityNotificationInProfile(
          profile,
          professionalId,
          enabled
        )
      );
    }

    mutateToggle({ professionalId, enabled });
  };

  return (
    <NavigationWrapper disableScroll>
      <Stack align="center" gap={0}>
        <Paper
          shadow="xl"
          radius="md"
          p="md"
          w="100%"
          maw={1200}
          mih={isTabletAndBelow ? scrollAreaHeight : undefined}
        >
          <Stack gap="xs">
            <Title order={3} fw="normal">
              Following
            </Title>
            <Group justify="space-between" align="flex-end" px="xs">
              <Text c="dimmed" fw={500}>
                Creator
              </Text>
              <Text c="dimmed" ta="right" fw={500}>
                Notifiy on New Slots
              </Text>
            </Group>
            <Divider />
            <ScrollArea
              h={Number(scrollAreaHeight?.replace('px', '')) - 100}
              viewportRef={viewportRef}
              data-testid="following-scroll-area"
            >
              <Stack gap={0}>
                {creators.map((creator) => (
                  <FollowerRow
                    key={creator.id}
                    creator={creator}
                    onNavigate={(id) =>
                      navigate(`${PageRoutes.PROFILE}?professionalId=${id}`)
                    }
                    onToggleChange={handleToggleChange}
                    checked={getToggleValue(creator)}
                    disabled={togglePending}
                  />
                ))}
                {!isLoading && creators.length === 0 && (
                  <Text c="dimmed" ta="center" py="lg">
                    You are not following any creators yet.
                  </Text>
                )}
                {(isLoading || isFetchingNextPage) && (
                  <FollowingPageSkeleton count={isLoading ? 8 : 3} />
                )}
              </Stack>
            </ScrollArea>
          </Stack>
        </Paper>
      </Stack>
      {toggleErrorToast && (
        <Notification
          color="red"
          title="Action failed"
          withCloseButton
          onClose={() => setToggleErrorToast('')}
          icon={<IconAlertCircle size={16} />}
          style={{
            position: 'fixed',
            right: 16,
            bottom: 16,
            zIndex: 400,
          }}
          data-testid="following-toggle-error-toast"
        >
          {toggleErrorToast}
        </Notification>
      )}
    </NavigationWrapper>
  );
};
